import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import type { User } from "@segment/analytics-next";
import { CookiesService } from "@services/CookiesService";
import type { CookieSetOptions } from "universal-cookie";

import type { AnalyticsPageTag, AnalyticsTag } from "@components/types";

import { fbCapiContext } from "@lib/analytics/fbCapiContext";
import { useAppContext } from "@lib/contexts/AppContext";
import { useSegment } from "@lib/contexts/SegmentContext";
import { resolveActiveExperiment } from "@pages/checkout/paymentFirst";

interface IUseAnalytics {
  analytics: {
    track: (tag: AnalyticsTag) => Promise<any>;
    page: (tag: AnalyticsPageTag) => Promise<any>;
    identify: (userId: string, payload: Object) => Promise<any>;
    getSegmentUser: () => Promise<User>;
  };
}

const useAnalytics = (): IUseAnalytics => {
  const [, setCookie] = useState<string | null>(null);
  const [, setExperimentCookie] = useCookies(["_fbc", "_fbp"]);
  const { analytics: segmentAnalytics } = useSegment();
  const {
    state: { experiment },
  } = useAppContext();

  useEffect(() => {
    if (experiment?.id && experiment?.variant !== undefined) {
      CookiesService.setExperimentCookie(
        (name: string, value: string, options?: CookieSetOptions) =>
          setExperimentCookie(name as any, value, options),
        experiment.id,
        experiment.variant.toString(),
      );
      setCookie(`${experiment.id}.${experiment.variant}`);
    }
  }, [experiment?.id, experiment?.variant]);

  // Resolved per call, not per render (OMG-2313). The payment-first arm is
  // identified by its own route, so on that route we derive the experiment from
  // the URL and ignore AppContext: the context value is set by an effect on
  // route mount and was missing from ~86% of variant events in production,
  // which silently biased every variant-filtered report. Any other experiment
  // still comes from context, which remains the general mechanism.
  const resolveGlobal = () => {
    const active = resolveActiveExperiment(
      experiment,
      import.meta.env.VITE_ENV,
    );
    return {
      experiment: active?.name,
      experiment_id: active?.id,
      variant: active?.variant_name,
      variant_id: active?.variant,
      env: import.meta.env.VITE_ENV,
    };
  };

  const page = (tag: AnalyticsPageTag) => {
    return segmentAnalytics.page(
      tag.page,
      { ...resolveGlobal(), ...tag.props },
      fbCapiContext(),
    );
  };

  const track = (tag: AnalyticsTag) =>
    segmentAnalytics.track(
      tag.event,
      { ...resolveGlobal(), ...tag.props },
      fbCapiContext(),
    );

  const getSegmentUser = () => segmentAnalytics.user();

  const identify = (userId: string, payload: Object) => {
    return segmentAnalytics.identify(userId, payload);
  };

  return {
    analytics: { page, track, identify, getSegmentUser },
  };
};

export default useAnalytics;
